import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Thumbnail,
  Checkbox,
  Box,
  Spinner,
  Scrollable,
  Button,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';

interface ShopifyProduct {
  id: number;
  title: string;
  handle: string;
  image?: string; // URL string from API
  images?: Array<{ id: number; src: string }>;
  variants?: Array<{
    id: number;
    price: string;
    compare_at_price?: string | null;
  }>;
}

export interface SelectedProduct {
  productId: number;
  variantId?: number;
  title: string;
  handle: string;
  imageUrl: string;
  price: number;
  quantity: number;
}

interface ProductPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (products: SelectedProduct[]) => void;
  existingProductIds?: number[];
  maxSelection?: number;
}

export function ProductPickerModal({
  open,
  onClose,
  onSelect,
  existingProductIds = [],
  maxSelection = 10,
}: ProductPickerModalProps) {
  const { t } = useTranslation();
  const { shop, shopInfo } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();

  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Load products when modal opens
  const loadProducts = useCallback(async () => {
    if (!shop || !open) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth<{ products: ShopifyProduct[] }>(
        '/api/shopify/products?limit=100'
      );
      setProducts(response?.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [shop, open, fetchWithAuth]);

  useEffect(() => {
    if (open) {
      loadProducts();
      setSelectedIds(new Set());
      setSearchQuery('');
    }
  }, [open, loadProducts]);

  // Filter products by search query
  const filteredProducts = products.filter((product) => {
    // Exclude already added products
    if (existingProductIds.includes(product.id)) return false;
    
    // Filter by search query
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      product.title.toLowerCase().includes(query) ||
      product.handle.toLowerCase().includes(query)
    );
  });

  const toggleSelection = useCallback((productId: number) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else if (newSet.size < maxSelection) {
        newSet.add(productId);
      }
      return newSet;
    });
  }, [maxSelection]);

  const handleConfirm = useCallback(() => {
    const selectedProducts: SelectedProduct[] = products
      .filter((p) => selectedIds.has(p.id))
      .map((p) => ({
        productId: p.id,
        variantId: p.variants?.[0]?.id,
        title: p.title,
        handle: p.handle,
        imageUrl: p.image || p.images?.[0]?.src || '',
        price: parseFloat(p.variants?.[0]?.price || '0'),
        quantity: 1,
      }));
    
    onSelect(selectedProducts);
    onClose();
  }, [products, selectedIds, onSelect, onClose]);

  // Format currency based on shop's currency
  const formatCurrency = (value: number) => {
    const currency = shopInfo?.currency || 'EUR';
    const localeMap: Record<string, string> = {
      'EUR': 'es-ES',
      'USD': 'en-US',
      'GBP': 'en-GB',
      'JPY': 'ja-JP',
      'CAD': 'en-CA',
      'AUD': 'en-AU',
      'CHF': 'de-CH',
      'CNY': 'zh-CN',
      'MXN': 'es-MX',
      'BRL': 'pt-BR',
    };
    const locale = localeMap[currency] || 'es-ES';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('bundles.selectProducts')}
      primaryAction={{
        content: t('bundles.addProductsCount', { count: selectedIds.size }),
        onAction: handleConfirm,
        disabled: selectedIds.size === 0,
      }}
      secondaryActions={[
        {
          content: t('common.cancel'),
          onAction: onClose,
        },
      ]}
      size="large"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TextField
            label={t('bundles.searchProducts')}
            labelHidden
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('bundles.searchProductsPlaceholder')}
            autoComplete="off"
            prefix={<SearchIcon />}
            clearButton
            onClearButtonClick={() => setSearchQuery('')}
          />

          {selectedIds.size > 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t('bundles.productsSelected', { count: selectedIds.size, max: maxSelection })}
            </Text>
          )}

          {isLoading ? (
            <Box padding="800">
              <InlineStack align="center">
                <Spinner size="large" />
                <Text as="p">{t('bundles.loadingProducts')}</Text>
              </InlineStack>
            </Box>
          ) : (
            <Scrollable style={{ height: '400px' }}>
              <BlockStack gap="200">
                {filteredProducts.length === 0 ? (
                  <Box padding="400">
                    <Text as="p" tone="subdued" alignment="center">
                      {searchQuery ? t('bundles.noProductsFound') : t('bundles.noProductsAvailable')}
                    </Text>
                  </Box>
                ) : (
                  filteredProducts.map((product) => {
                    const isSelected = selectedIds.has(product.id);
                    const price = parseFloat(product.variants?.[0]?.price || '0');
                    const imageUrl = product.image || product.images?.[0]?.src || '';

                    return (
                      <Box
                        key={product.id}
                        padding="300"
                        borderRadius="200"
                        background={isSelected ? 'bg-surface-selected' : 'bg-surface'}
                      >
                        <InlineStack gap="300" align="start" blockAlign="center">
                          <Checkbox
                            label=""
                            labelHidden
                            checked={isSelected}
                            onChange={() => toggleSelection(product.id)}
                            disabled={!isSelected && selectedIds.size >= maxSelection}
                          />
                          <Thumbnail
                            source={imageUrl || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}
                            alt={product.title}
                            size="small"
                          />
                          <Box minWidth="200px">
                            <BlockStack gap="050">
                              <Text as="span" variant="bodyMd" fontWeight="semibold" truncate>
                                {product.title}
                              </Text>
                              <Text as="span" variant="bodySm" tone="subdued">
                                {formatCurrency(price)}
                              </Text>
                            </BlockStack>
                          </Box>
                          <Button
                            size="slim"
                            onClick={() => toggleSelection(product.id)}
                            disabled={!isSelected && selectedIds.size >= maxSelection}
                          >
                            {isSelected ? t('common.remove') : t('common.add')}
                          </Button>
                        </InlineStack>
                      </Box>
                    );
                  })
                )}
              </BlockStack>
            </Scrollable>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
